// QueryParams is a module of pure functions for parsing, building, and
// transforming Explorer query state. Consumers work with plain
// { page, filters, sortOrder, ... } objects produced by `QueryParams.parse`
// — typically `QueryParams.parse(searchParams.toString())` inside a component
// that calls `useSearchParams()`.

import { cloneDeep, filter, flatMap, flow, isArray, mapKeys, mapValues, pick } from 'lodash-es';
import QueryString from 'query-string';
import config from 'util/site_config';

export const DEFAULT_SORT = 'score';
export const DEFAULT_MENTION_SOURCE_ORDER = 'mentions_count_desc';
export const DEFAULT_VIEW = 'grid';
export const BASE_PATH = '/explorer';
export const BASE_JSON_PATH = `${BASE_PATH}/json_data`;

// Top-level params handled outside of `filters` — these are pulled directly
// from the query string into named state fields, so they must not leak into
// the filters bag.
const TOP_LEVEL_PARAMS = ['order', 'view', 'mention_source_order', 'per_page'];

// The list of param names treated as filters (i.e. everything in
// site_config.queryFilterParams except the top-level params above), with
// array variants (`foo[]`) included alongside the bare names. Computed
// once at module load — config.queryFilterParams is static.
const FILTER_PARAM_NAMES = flow(
  (fp) => filter(fp, (p) => !TOP_LEVEL_PARAMS.includes(p)),
  (fp) => flatMap(fp, (p) => [p, `${p}[]`])
)(config.queryFilterParams);

export function parse(searchString = '') {
  const params = QueryString.parse((searchString || '').replace(/^\?/, ''));

  return {
    page: Number.parseInt(params.page, 10) || 1,
    perPage: Number.parseInt(params.per_page, 10) || undefined,
    sortOrder: params.order || DEFAULT_SORT,
    mentionSourceOrder: params.mention_source_order || DEFAULT_MENTION_SOURCE_ORDER,
    showDetails: Number.parseInt(params.show_details, 10) || undefined,
    view: params.view || DEFAULT_VIEW,
    filters: pick(params, FILTER_PARAM_NAMES)
  };
}

function assignUnfiltered(target, state) {
  if (state.page !== 1) target.page = state.page;
  if (state.sortOrder !== DEFAULT_SORT) target.order = state.sortOrder;
  if (state.mentionSourceOrder !== DEFAULT_MENTION_SOURCE_ORDER) target.mention_source_order = state.mentionSourceOrder;
  if (state.view !== DEFAULT_VIEW) target.view = state.view;
  if (state.showDetails) target.show_details = state.showDetails;
  if (state.perPage) target.per_page = state.perPage;
  return target;
}

export function build(state) {
  const params = cloneDeep(state.filters || {});
  return assignUnfiltered(params, state);
}

export function buildSearch(state) {
  return QueryString.stringify(build(state));
}

// React-Router-friendly `search` string for a location: includes the leading
// `?` when there are params, empty string otherwise. Use this anywhere a
// dangling `?` would otherwise leak into a URL.
export function searchFor(state) {
  const search = buildSearch(state);
  return search ? `?${search}` : '';
}

export function buildPOSTParams(state) {
  const cleanFilters = flow(
    (f) => mapValues(f, (value, key) => (key.endsWith('[]') && !isArray(value) ? [value] : value)),
    (f) => mapKeys(f, (_value, key) => key.replace('[]', ''))
  )(state.filters || {});

  const params = assignUnfiltered(cleanFilters, state);
  // Drop UI-only state: page is reset by saved-search restore, show_details
  // is just the open detail popover.
  delete params.page;
  delete params.show_details;
  return params;
}

// Used for tab/route navigation — drops page so the new tab always starts at
// page 1.
export function locationFor(state, pathname) {
  const params = build(state);
  delete params.page;
  const search = QueryString.stringify(params);
  return { search: search ? `?${search}` : '', pathname };
}

export function withPage(state, page) {
  return { ...state, page };
}

export function withPerPage(state, perPage) {
  return { ...state, perPage };
}

export function withFilters(state, filters) {
  return { ...state, filters, page: 1 };
}

export function withSortOrder(state, sortOrder) {
  return { ...state, sortOrder, page: 1 };
}

export function withMentionSourceOrder(state, mentionSourceOrder) {
  const next = { ...state, mentionSourceOrder, page: 1, filters: { ...(state.filters || {}) } };
  if (mentionSourceOrder === 'followers_desc') {
    next.filters['mention_sources_types[]'] = ['type:tw'];
  }
  return next;
}

export function withShowDetails(state, showDetails) {
  return { ...state, showDetails };
}

export function withView(state, view) {
  return { ...state, view };
}
